import { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  alpha,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import CustomInput from "../../FormFields/CustomInput/CustomInput";
import CustomDate from "../../CustomForm/CustomDate";
import CustomCheckbox from "../../CustomForm/CustomCheckbox";
import CustomAutoComplete from "../../FormFields/CustomSelect/CustomAutoComplete";
import { QK } from "../../../base/qk";
import { useQuery } from "react-query";
import { searchCandidates } from "../../../api/services/candidate";

const SocialChcNotificationInfo = ({ formData, handleChange }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [careerSearch, setCareerSearch] = useState("");

  const { data: careers, isLoading: careersLoading } = useQuery(
    [QK.searchCareers, careerSearch],
    () => searchCandidates(careerSearch),
    { enabled: true, keepPreviousData: true }
  );

  const careerOptions = (careers || []).map((c) => ({
    id: c.id,
    name: `${c.first_name} ${c.last_name}`.trim(),
  }));

  const updateSocialData = (updates) => {
    handleChange({
      target: {
        name: "social_services_notification",
        value: { ...formData.social_services_notification, ...updates },
      },
    });
  };

  const handleSocialChange = (e) => {
    const { name, value, type, checked } = e.target;
    updateSocialData({ [name]: type === "checkbox" ? checked : value });
  };

  const handleInformedByChange = (selectedId) => {
    updateSocialData({ informed_by: selectedId });
  };

  const isNotRequired =
    formData.social_services_notification?.not_required || false;

  return (
    <Accordion
      expanded={expanded}
      onChange={(e, isExpanded) => setExpanded(isExpanded)}
      sx={{
        boxShadow: "none",
        border: "1px solid #EAECF0",
        borderRadius: "12px !important",
        overflow: "hidden",
        "&:before": { display: "none" },
        "&.Mui-expanded": { margin: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#475467" }} />}
        sx={{
          backgroundColor: "#FAFBFC",
          minHeight: "56px",
          "&.Mui-expanded": { minHeight: "56px" },
          "& .MuiAccordionSummary-content": {
            margin: "12px 0",
            "&.Mui-expanded": { margin: "12px 0" },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LocalHospitalIcon
                sx={{ fontSize: 18, color: theme.palette.primary.main }}
              />
            </Box>
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                color="#1A1A2E"
                fontSize="0.9rem"
              >
                Social CHC Notification
              </Typography>
              <Typography variant="caption" color="#6B7280" fontSize="0.7rem">
                Social services / CHC notification
              </Typography>
            </Box>
          </Box>

          <Box onClick={(e) => e.stopPropagation()}>
            <CustomCheckbox
              name="not_required"
              label="Not required"
              isInitiallyChecked={isNotRequired}
              onClick={(checked) =>
                updateSocialData({ not_required: checked })
              }
            />
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 3, bgcolor: "#FFFFFF" }}>
        {isNotRequired && (
          <Box
            sx={{
              p: 2,
              borderRadius: "10px",
              bgcolor: "#F9FAFB",
              border: "1px solid #EAECF0",
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              color="#6B7280"
              fontSize="0.8rem"
              fontWeight={500}
            >
              Social CHC notification marked as <strong>Not Required</strong>{" "}
              for this incident.
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
            opacity: isNotRequired ? 0.5 : 1,
            pointerEvents: isNotRequired ? "none" : "auto",
          }}
        >
          <CustomInput
            name="reference_number"
            label="Social Reference Number"
            value={
              formData.social_services_notification?.reference_number || ""
            }
            onChange={handleSocialChange}
          />

          <CustomInput
            name="contact_name"
            label="Contact Name"
            value={formData.social_services_notification?.contact_name || ""}
            onChange={handleSocialChange}
          />

          <CustomInput
            name="contact_telephone"
            label="Contact Telephone"
            value={
              formData.social_services_notification?.contact_telephone || ""
            }
            onChange={handleSocialChange}
          />

          <CustomAutoComplete
            name="informed_by"
            label="Who Informed"
            onInputChange={setCareerSearch}
            options={careerOptions}
            isLoading={careersLoading}
            inputValue={careerSearch}
            value={formData.social_services_notification?.informed_by || ""}
            onChange={handleInformedByChange}
          />

          <CustomDate
            name="date_informed"
            label="Date Informed"
            value={formData.social_services_notification?.date_informed || ""}
            onChange={handleSocialChange}
          />

          <Box sx={{ gridColumn: { xs: "1", md: "1 / -1" } }}>
            <CustomInput
              name="notes"
              label="Notes"
              value={formData.social_services_notification?.notes || ""}
              onChange={handleSocialChange}
              multiline
              rows={3}
            />
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default SocialChcNotificationInfo;